import SEO from '../components/SEO';
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { faqs } from '../mock';

const FAQ = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="FAQ débarras : tarifs, délais, déroulement | DEBECO"
        description="Réponses aux questions fréquentes sur nos services de débarras en Savoie et Haute-Savoie. Tarifs, délais, zones d'intervention."
        canonical="/faq"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
            }))
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {"@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://debeco.fr/"},
              {"@type": "ListItem", "position": 2, "name": "FAQ"}
            ]
          }
        ]}
      />
      {/* Hero - Harmonized without icon */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Questions fréquentes</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Toutes les réponses à vos questions sur nos services de débarras
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 shadow-xl">
              <CardContent className="p-8">
                <Accordion type="single" collapsible className="w-full space-y-4">
                  {faqs.map((faq) => (
                    <AccordionItem 
                      key={faq.id} 
                      value={faq.id}
                      className="border-2 border-gray-200 rounded-lg px-6 data-[state=open]:border-emerald-500 transition-all"
                    >
                      <AccordionTrigger className="text-left hover:no-underline py-5">
                        <span className="font-semibold text-lg text-blue-900 pr-4">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-700 pb-5 leading-relaxed">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Quick Info */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4">Informations pratiques</h2>
              <p className="text-gray-600">Ce que vous devez savoir sur nos services</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-2xl">⏱</span>
                  </div>
                  <h3 className="font-bold text-xl text-blue-900 mb-3">Délai d'intervention</h3>
                  <p className="text-gray-700">
                    Nous intervenons en <strong>24 à 48 heures</strong> sur l'ensemble de nos zones (Annecy, Genevois, Savoie). Pour les urgences, contactez-nous directement.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-2xl">€</span>
                  </div>
                  <h3 className="font-bold text-xl text-blue-900 mb-3">Devis gratuit</h3>
                  <p className="text-gray-700">
                    Tous nos devis sont <strong>gratuits et sans engagement</strong>. Nous nous déplaçons pour évaluer le volume et vous proposer un tarif personnalisé.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-900 to-blue-700 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-2xl">♻</span>
                  </div>
                  <h3 className="font-bold text-xl text-blue-900 mb-3">Recyclage</h3>
                  <p className="text-gray-700">
                    Nous trions et recyclons systématiquement. Les objets valorisables sont donnés aux associations ou revendus dans une logique d'économie circulaire.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-emerald-500 rounded-xl flex items-center justify-center mb-4">
                    <span className="text-2xl">🛡</span>
                  </div>
                  <h3 className="font-bold text-xl text-blue-900 mb-3">Assurance</h3>
                  <p className="text-gray-700">
                    Notre équipe est <strong>assurée et formée</strong> pour tous types d'interventions. Nous travaillons en toute sécurité et dans le respect de votre bien.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Still have questions */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <Card className="border-2 border-blue-900">
              <CardContent className="p-8">
                <h2 className="text-2xl md:text-3xl font-bold text-blue-900 mb-4">
                  Vous avez encore des questions ?
                </h2>
                <p className="text-gray-700 mb-6">
                  Notre équipe est à votre disposition pour répondre à toutes vos interrogations.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Nous contacter
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-gray-50" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Aller plus loin</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/services" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Nos services de débarras</p>
              <p className="text-sm text-gray-600 mt-1">Maison, appartement, cave, succession, professionnel</p>
            </Link>
            <Link to="/blog/prix-debarras-maison-annecy" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Guide des prix de débarras</p>
              <p className="text-sm text-gray-600 mt-1">Fourchettes tarifaires détaillées pour Annecy</p>
            </Link>
            <Link to="/devis" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Estimation gratuite en ligne</p>
              <p className="text-sm text-gray-600 mt-1">Obtenez un devis en quelques minutes</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Prêt pour un débarras ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Demandez votre devis gratuit et bénéficiez d'une intervention rapide
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6">
              Obtenir un devis
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
